import React, { useEffect } from 'react';

import IncentiveBanner from '../../../phone_calls/incentive_banner';

import styles from './index.module.css';

function PhoneIcon() {
  return (
    <svg
      className={styles.header_icon_svg}
      viewBox='0 0 256 256'
      fill='currentColor'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
    >
      <path d='M231.88,175.08A56.26,56.26,0,0,1,176,224C96.6,224,32,159.4,32,80A56.26,56.26,0,0,1,80.92,24.12a16,16,0,0,1,16.62,9.52l21.12,47.15,0,.12A16,16,0,0,1,117.39,96c-.18.27-.37.52-.57.77L96,121.45c7.49,15.22,23.41,31,38.83,38.51l24.34-20.71a8.12,8.12,0,0,1,.75-.56,16,16,0,0,1,15.17-1.4l.13.06,47.11,21.11A16,16,0,0,1,231.88,175.08Z' />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      className={styles.action_icon_svg}
      viewBox='0 0 24 24'
      fill='none'
      xmlns='http://www.w3.org/2000/svg'
      aria-hidden='true'
    >
      <path
        d='M8 2.5V5.5M16 2.5V5.5M4 9H20M6.5 4H17.5C18.9 4 20 5.1 20 6.5V17.5C20 18.9 18.9 20 17.5 20H6.5C5.1 20 4 18.9 4 17.5V6.5C4 5.1 5.1 4 6.5 4Z'
        stroke='currentColor'
        strokeWidth='1.8'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
      <path d='M3 3L15 15M15 3L3 15' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg' aria-hidden='true'>
      <path
        d='M1.31 6H10.69M6 1.31L10.69 6L6 10.69'
        stroke='currentColor'
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  );
}

function DrawerActionButton({ icon, title, subtitle, onClick, disabled, ariaLabel }) {
  return (
    <button
      type='button'
      className={styles.action_btn}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel || title}
    >
      <span className={styles.action_icon} aria-hidden='true'>
        {icon}
      </span>
      <span className={styles.action_text}>
        <span className={styles.action_title}>{title}</span>
        {subtitle && <span className={styles.action_subtitle}>{subtitle}</span>}
      </span>
    </button>
  );
}

export default function CallsDrawer({
  open,
  otherUserFirstName,
  afterHoursLabel,
  callable = true,
  isMentor = false,
  incentive = null,
  onClose,
  onRequestCall,
  onScheduleCall,
  onHowCallsWork,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  if (!open) return null;

  const requestTitle = isMentor ? 'Suggest a Call' : 'Request a Call';
  const baseRequestSubtitle = isMentor
    ? `Send ${otherUserFirstName} time options`
    : `${otherUserFirstName} gets a text, 15 min to respond`;
  const afterHoursSubtitle = afterHoursLabel ? `Available after ${afterHoursLabel}` : 'Available after 8AM';
  const requestSubtitle = !isMentor && !callable ? afterHoursSubtitle : baseRequestSubtitle;

  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget) onClose?.();
  };

  return (
    <div className={styles.backdrop} onClick={handleBackdropClick}>
      <aside className={styles.drawer} role='dialog' aria-modal='true' aria-labelledby='calls-drawer-title'>
        <header className={styles.header}>
          <div className={styles.header_identity}>
            <span className={styles.header_icon}>
              <PhoneIcon />
            </span>
            <div className={styles.header_text}>
              <h2 id='calls-drawer-title' className={styles.header_name}>
                {otherUserFirstName}
              </h2>
              <p className={styles.header_subtitle}>Calls</p>
            </div>
          </div>
          <button type='button' className={styles.close_btn} onClick={onClose} aria-label='Close calls menu'>
            <CloseIcon />
          </button>
        </header>

        <div className={styles.body}>
          {!isMentor && incentive && (
            <IncentiveBanner
              incentive={incentive}
              mentorName={otherUserFirstName}
              className={styles.incentive_banner}
            />
          )}
          <div className={styles.actions}>
            <DrawerActionButton
              icon={<PhoneIcon />}
              title={requestTitle}
              subtitle={requestSubtitle}
              onClick={onRequestCall}
              disabled={!isMentor && !callable}
            />
            <DrawerActionButton
              icon={<CalendarIcon />}
              title='Schedule a Call'
              subtitle='Pick times that work for you'
              onClick={onScheduleCall}
            />
          </div>

          {onHowCallsWork && (
            <div className={styles.footer}>
              <span className={styles.footer_prefix}>New here?</span>
              <button type='button' className={styles.footer_link} onClick={onHowCallsWork}>
                See how calls work
                <ArrowRightIcon />
              </button>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
